import * as React from "react"

/**
 * Custom hook that listens to a CSS media query string and returns a boolean matching state.
 * Safely defaults to false if window is undefined (SSR/Node/testing environments without window).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(() => {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      return window.matchMedia(query).matches
    }
    return false
  })

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return
    }

    const mediaQueryList = window.matchMedia(query)
    setMatches(mediaQueryList.matches)

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", listener)
      return () => mediaQueryList.removeEventListener("change", listener)
    } else {
      // Fallback for older browsers / JSDOM implementations
      mediaQueryList.addListener(listener)
      return () => mediaQueryList.removeListener(listener)
    }
  }, [query])

  return matches
}

/**
 * Returns true if the viewport width is below 640px (Tailwind 'sm' breakpoint).
 */
export function useIsMobile(breakpointPx: number = 640): boolean {
  return useMediaQuery(`(max-width: ${breakpointPx - 1}px)`)
}
